import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Database, HardDrive, Shield, Clock, Users, AlertTriangle, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';

const DataManagementTab = ({
  backups, createBackup, backingUp, fetchBackups,
  autoBackupStatus, toggleAutoBackup, togglingAutoBackup,
  dataIntegrity, fetchDataIntegrity, cleanupTestData, cleaningUp
}) => (
  <div className="space-y-6">
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div><CardTitle className="flex items-center gap-2"><Database className="h-5 w-5 text-[#E8722A]" />Database Backups</CardTitle><CardDescription>Create and manage snapshots of your critical data</CardDescription></div>
        <Button onClick={createBackup} disabled={backingUp} className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="create-backup-btn">
          {backingUp ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>) : (<><HardDrive className="mr-2 h-4 w-4" />Create Backup</>)}
        </Button>
      </CardHeader>
      <CardContent>
        {backups.length === 0 ? (
          <div className="text-center py-8 text-[#666666]">
            <Database className="h-12 w-12 mx-auto mb-3 text-gray-300" /><p className="font-medium">No backups yet</p><p className="text-sm mt-1">Create your first backup to protect your data</p>
            <Button variant="outline" onClick={fetchBackups} className="mt-3" data-testid="refresh-backups-btn"><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Backup ID</TableHead><TableHead>Reason</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Collections</TableHead></TableRow></TableHeader>
            <TableBody>
              {backups.map((b) => (
                <TableRow key={b.backup_id} data-testid={`backup-row-${b.backup_id}`}>
                  <TableCell className="font-mono text-xs">{b.backup_id}</TableCell><TableCell className="capitalize">{b.reason}</TableCell>
                  <TableCell className="text-[#666666]">{new Date(b.timestamp).toLocaleString()}</TableCell>
                  <TableCell><Badge className={b.status === 'complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{b.status}</Badge></TableCell>
                  <TableCell className="text-sm text-[#666666]">{Object.keys(b.collections || {}).length} collections</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-[#4B2D6E]" />Scheduled Auto-Backup</CardTitle><CardDescription>Automatically back up your data daily and keep the last 7 snapshots</CardDescription></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-[#F5F5F5] rounded-lg border">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${autoBackupStatus?.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
            <div><p className="font-medium text-[#333333]">{autoBackupStatus?.enabled ? 'Auto-Backup Active' : 'Auto-Backup Disabled'}</p><p className="text-sm text-[#666666]">{autoBackupStatus?.schedule || 'Daily at 2:00 AM ET'} &middot; Keeps last {autoBackupStatus?.retention_days || 7} backups</p></div>
          </div>
          <Switch checked={autoBackupStatus?.enabled || false} onCheckedChange={(checked) => toggleAutoBackup(checked)} disabled={togglingAutoBackup} data-testid="auto-backup-toggle" />
        </div>
        {autoBackupStatus?.enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-white border rounded-lg"><p className="text-sm text-[#666666]">Auto-backups stored</p><p className="text-2xl font-bold text-[#4B2D6E]">{autoBackupStatus.auto_backup_count || 0}<span className="text-sm font-normal text-[#666666]"> / {autoBackupStatus.retention_days}</span></p></div>
            <div className="p-3 bg-white border rounded-lg"><p className="text-sm text-[#666666]">Last auto-backup</p><p className="text-sm font-medium text-[#333333]">{autoBackupStatus.last_auto_backup ? new Date(autoBackupStatus.last_auto_backup.timestamp).toLocaleString() : 'Not yet run'}</p>{autoBackupStatus.last_auto_backup && <Badge className="mt-1 bg-green-100 text-green-800">{autoBackupStatus.last_auto_backup.status}</Badge>}</div>
          </div>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-[#4B2D6E]" />Data Integrity</CardTitle><CardDescription>Overview of real vs. test data in your system</CardDescription></div>
        <Button variant="outline" onClick={fetchDataIntegrity} data-testid="refresh-integrity-btn"><RefreshCw className="mr-2 h-4 w-4" />Refresh Stats</Button>
      </CardHeader>
      <CardContent>
        {!dataIntegrity ? (
          <div className="text-center py-8 text-[#666666]"><Shield className="h-12 w-12 mx-auto mb-3 text-gray-300" /><p>Click "Refresh Stats" to load data integrity information</p></div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="real-candidates-stat">
                <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-5 w-5 text-green-600" /><p className="text-sm font-medium text-green-800">Real Candidates</p></div>
                <p className="text-3xl font-bold text-green-700">{dataIntegrity.real_candidates}</p><p className="text-xs text-green-600 mt-1">{dataIntegrity.real_with_score} with AI scores</p>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="test-candidates-stat">
                <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-5 w-5 text-yellow-600" /><p className="text-sm font-medium text-yellow-800">Test Candidates</p></div>
                <p className="text-3xl font-bold text-yellow-700">{dataIntegrity.test_candidates}</p>
                {dataIntegrity.test_candidates > 0 && <Button variant="link" size="sm" className="text-yellow-700 p-0 h-auto mt-1 text-xs" onClick={cleanupTestData} disabled={cleaningUp} data-testid="cleanup-test-btn">{cleaningUp ? 'Cleaning...' : 'Clean up test data'}</Button>}
              </div>
              <div className="p-4 bg-[#4B2D6E]/5 border border-[#4B2D6E]/20 rounded-lg" data-testid="total-candidates-stat">
                <div className="flex items-center gap-2 mb-1"><Users className="h-5 w-5 text-[#4B2D6E]" /><p className="text-sm font-medium text-[#4B2D6E]">Total</p></div>
                <p className="text-3xl font-bold text-[#4B2D6E]">{dataIntegrity.total_candidates}</p><p className="text-xs text-[#666666] mt-1">{dataIntegrity.real_with_answers} with answers</p>
              </div>
            </div>
            {dataIntegrity.collections && (
              <div>
                <h4 className="font-semibold text-[#333333] mb-3">Collection Sizes</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(dataIntegrity.collections).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between p-3 bg-[#F5F5F5] rounded-lg"><span className="text-sm font-medium capitalize">{name.replace(/_/g, ' ')}</span><Badge variant="outline">{count}</Badge></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>

    <div className="p-4 bg-[#4B2D6E]/5 rounded-lg border border-[#4B2D6E]/20">
      <div className="flex items-start gap-3">
        <Shield className="h-5 w-5 text-[#4B2D6E] mt-0.5 flex-shrink-0" />
        <div><p className="font-medium text-[#4B2D6E] mb-1">Data Protection</p><p className="text-sm text-[#666666]">Backups snapshot all critical collections. We recommend creating a backup before any major changes. Test data can be cleaned up without affecting real candidates.</p></div>
      </div>
    </div>
  </div>
);

export { DataManagementTab };
